import React, { forwardRef, useEffect, useRef } from 'react';
import { NativeSyntheticEvent, StyleSheet, TargetedEvent, TouchableHighlight } from 'react-native';
import { useRecoilValue } from 'recoil';

import { Theme } from '@constants';
import { screenReaderEnabledState } from '@store';

import { FOCUS_NOW } from '../../../helpers/Tools';
import { TWCTile, TWCTileProps } from '../TWCTile/TWCTile';

export const TWCAccessibleContent = forwardRef<TouchableHighlight, TWCAccessibleContentProps>(
  ({ onFocus, onBlur, disabledScreenreaderFocus, accessibilityRole, ...properties }, reference) => {
    const screenReaderEnabled = useRecoilValue(screenReaderEnabledState);

    const isFocused = useRef(false);

    const updateOnFocus = (event: NativeSyntheticEvent<TargetedEvent>) => {
      onFocus?.(event);
      if (screenReaderEnabled) {
        isFocused.current = true;
      } else {
        if (disabledScreenreaderFocus) {
          FOCUS_NOW(disabledScreenreaderFocus);
          isFocused.current = false;
        }
      }
    };

    const updatedOnBlur = (event: NativeSyntheticEvent<TargetedEvent>) => {
      onBlur?.(event);
      isFocused.current = false;
    };

    useEffect(() => {
      if (disabledScreenreaderFocus && isFocused.current && !screenReaderEnabled) {
        FOCUS_NOW(disabledScreenreaderFocus);
        isFocused.current = false;
      }
    }, [screenReaderEnabled, disabledScreenreaderFocus]);

    return (
      <TWCTile
        {...properties}
        accessibilityRole={accessibilityRole ? accessibilityRole : 'text'}
        borderStyleFocused={styles.borderShift}
        ref={reference}
        useFocusBorder={screenReaderEnabled}
        useLinearGradient={false}
        onBlur={updatedOnBlur}
        onFocus={updateOnFocus}
      />
    );
  },
);

const styles = StyleSheet.create({
  borderShift: {
    marginLeft: -Theme.px(8),
    marginTop: -Theme.px(8),
  },
});

type TWCAccessibleContentProps = {
  disabledScreenreaderFocus: unknown;
} & TWCTileProps;
